import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Camera, 
  Package,
  Barcode,
  Trash2,
  Minus
} from "lucide-react";

const API = "/api";

export default function StokCikarPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [depolar, setDepolar] = useState([]);
  const [selectedDepo, setSelectedDepo] = useState("");
  const [barkodInput, setBarkodInput] = useState("");
  const [urunler, setUrunler] = useState([]);
  const barkodRef = useRef(null);

  const toplamAdet = urunler.reduce((sum, u) => sum + u.miktar, 0);
  const urunCesidi = urunler.length;

  useEffect(() => {
    fetchDepolar();
  }, [token]);

  useEffect(() => {
    if (barkodRef.current) {
      barkodRef.current.focus();
    }
  }, [urunler]);

  const fetchDepolar = async () => {
    try {
      const response = await axios.get(`${API}/nebim/depolar?token=${token}`);
      if (response.data.success) {
        setDepolar(response.data.depolar || []);
      }
    } catch (error) {
      console.error("Depolar alınamadı:", error);
    }
  };

  const handleBarkodEkle = async () => {
    if (!barkodInput.trim()) return;
    if (!selectedDepo) {
      toast.error("Lütfen depo seçin");
      return;
    }

    try {
      const response = await axios.get(`${API}/nebim/urun-by-barkod?token=${token}&barkod=${barkodInput}`);
      
      if (response.data.success && response.data.urun) {
        const urun = response.data.urun;
        // API'den gelen field isimlerini normalize et
        const productCode = urun.Itemcode || urun.ItemCode || urun.ProductCode || "";
        const productName = urun.ItemDescription || urun.ProductName || "";
        const colorDesc = urun.ColorDescription || urun.ColorCode || "";
        const sizeCode = urun.ItemDim1Code || "-";
        
        const existingIndex = urunler.findIndex(u => u.barkod === barkodInput);
        if (existingIndex >= 0) {
          const updated = [...urunler];
          updated[existingIndex].miktar += 1;
          setUrunler(updated);
        } else {
          setUrunler([...urunler, {
            barkod: barkodInput,
            urunKodu: productCode,
            urunAdi: productName,
            renk: colorDesc,
            beden: sizeCode,
            miktar: 1
          }]);
        }
        
        setBarkodInput("");
        toast.success(`${productName || barkodInput} eklendi`);
      } else {
        toast.error("Ürün bulunamadı");
      }
    } catch (error) {
      toast.error("Ürün aranırken hata oluştu");
    }
  };

  const handleMiktarDegistir = (index, delta) => {
    const updated = [...urunler];
    updated[index].miktar = Math.max(1, updated[index].miktar + delta);
    setUrunler(updated);
  };

  const handleUrunSil = (index) => {
    setUrunler(urunler.filter((_, i) => i !== index));
  };

  const handleStokCikisi = async () => {
    if (!selectedDepo) {
      toast.error("Lütfen depo seçin");
      return;
    }
    if (urunler.length === 0) {
      toast.error("Lütfen ürün ekleyin");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/nebim/stok-cikis?token=${token}`, {
        depo_kodu: selectedDepo,
        urunler: urunler.map(u => ({
          barkod: u.barkod,
          miktar: u.miktar
        }))
      });

      if (response.data.success) {
        toast.success(`${toplamAdet} adet ürün stok çıkışı yapıldı`);
        setUrunler([]);
      } else {
        toast.error(response.data.error || "Stok çıkışı başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Stok çıkışı hatası");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleBarkodEkle();
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="stok-cikar-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stok Çıkar</h1>
          <p className="text-slate-500 text-sm">Depodan stok çıkış işlemi</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Depo Seçimi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Stok Çıkılacak Depo</Label>
                <Select value={selectedDepo} onValueChange={setSelectedDepo}>
                  <SelectTrigger data-testid="select-depo">
                    <SelectValue placeholder="Depo Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {depolar.map((depo) => (
                      <SelectItem key={depo.kod} value={depo.kod}>
                        {depo.ad} ({depo.kod})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Barcode className="w-5 h-5" />
                Barkod Okutma
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button className="w-full bg-red-600 hover:bg-red-700" size="lg">
                <Camera className="w-5 h-5 mr-2" />
                Kamera ile Okut
              </Button>
              
              <div className="flex gap-2">
                <Input
                  ref={barkodRef}
                  placeholder="veya manuel barkod girin"
                  value={barkodInput}
                  onChange={(e) => setBarkodInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  data-testid="barkod-input"
                />
                <Button onClick={handleBarkodEkle} className="bg-red-600 hover:bg-red-700">
                  Ekle
                </Button>
              </div>

              {urunler.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Ürün eklemek için barkod okutun</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {urunler.map((urun, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{urun.urunAdi}</p>
                        <p className="text-xs text-slate-500">
                          {urun.urunKodu} | {urun.renk} | {urun.beden}
                        </p>
                        <p className="text-xs text-slate-400">{urun.barkod}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleMiktarDegistir(idx, -1)}
                        >
                          -
                        </Button>
                        <span className="w-10 text-center font-medium">{urun.miktar}</span>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleMiktarDegistir(idx, 1)}
                        >
                          +
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleUrunSil(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Stok Çıkış Özeti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Toplam Ürün Adedi</span>
                  <span className="text-3xl font-bold text-red-600">{toplamAdet}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Ürün Çeşidi:</span>
                  <span>{urunCesidi}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Kaynak Depo:</span>
                  <span>{selectedDepo || "-"}</span>
                </div>
              </div>

              <Button 
                className="w-full bg-red-600 hover:bg-red-700" 
                size="lg"
                disabled={loading || urunler.length === 0 || !selectedDepo}
                onClick={handleStokCikisi}
                data-testid="btn-stok-cikisi"
              >
                {loading ? "İşleniyor..." : "Stok Çıkışı Yap"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
